/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  DeleteOutline,
  Search,
  Star,
  StarBorder,
  UploadFile,
} from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  LinearProgress,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'

import type {
  CarouselImage,
  ImageSearchResult,
  ProductFormMode,
} from './ProductDialog.types'

type ProductDialogImagePanelProps = {
  mode: ProductFormMode
  saving: boolean
  productImagesLoading: boolean
  imageSearchLoading: boolean
  imageMutating: boolean
  imageMutationMessage: string | null
  imageDeletingId: string | null
  primaryImageUri?: string | null
  effectivePrimaryImageId: string | null
  marketingImages: CarouselImage[]
  imageSearchQuery: string
  imageSearchResults: ImageSearchResult[]
  imageSearchOpen: boolean
  uploadInputRef: React.RefCallback<HTMLInputElement>
  imageSearchAnchorEl: HTMLDivElement | null
  imageSearchAnchorRef: React.RefCallback<HTMLDivElement>
  onImageSearchQueryChange: (value: string) => void
  onSearch: () => void
  onCloseSearch: () => void
  onUploadClick: () => void
  onUploadInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onUseSearchResult: (result: ImageSearchResult) => void
  onSelectPrimaryImage: (image: CarouselImage) => void
  onDeleteImage: (imageId: string) => void
}

function ProductDialogImagePanel({
  mode,
  saving,
  productImagesLoading,
  imageSearchLoading,
  imageMutating,
  imageMutationMessage,
  imageDeletingId,
  primaryImageUri,
  effectivePrimaryImageId,
  marketingImages,
  imageSearchQuery,
  imageSearchResults,
  imageSearchOpen,
  uploadInputRef,
  imageSearchAnchorEl,
  imageSearchAnchorRef,
  onImageSearchQueryChange,
  onSearch,
  onCloseSearch,
  onUploadClick,
  onUploadInputChange,
  onUseSearchResult,
  onSelectPrimaryImage,
  onDeleteImage,
}: ProductDialogImagePanelProps) {
  const { t } = useT()

  return (
    <Box sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2">
          {t('correction.productDialog.imagesTitle', 'Marketing images')}
        </Typography>
        <Box
          sx={theme => ({
            border: '1px solid',
            borderColor: theme.palette.divider,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          })}
        >
          <Box
            sx={{
              aspectRatio: '1 / 1',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {mode === 'edit' && productImagesLoading ? (
              <CircularProgress size={28} />
            ) : primaryImageUri ? (
              <Box
                component="img"
                src={primaryImageUri}
                alt={t(
                  'correction.productDialog.primaryImageAlt',
                  'Primary marketing image',
                )}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(
                  'correction.productDialog.noMarketingImage',
                  'No marketing image selected',
                )}
              </Typography>
            )}
          </Box>
        </Box>
        {imageMutating ? <LinearProgress /> : null}
        {imageMutationMessage ? (
          <Typography variant="body2" color="text.secondary">
            {imageMutationMessage}
          </Typography>
        ) : null}
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={onUploadInputChange}
        />
        <Stack
          ref={imageSearchAnchorRef}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
        >
          <TextField
            label={t(
              'correction.productDialog.imageSearch',
              'Search marketing images',
            )}
            value={imageSearchQuery}
            onChange={event => onImageSearchQueryChange(event.target.value)}
            disabled={saving || imageSearchLoading || imageMutating}
            fullWidth
          />
          <Button
            variant="outlined"
            startIcon={<Search />}
            onClick={onSearch}
            disabled={saving || imageSearchLoading || imageMutating}
            sx={{
              whiteSpace: 'nowrap',
              justifyContent: 'center',
              gap: 0.75,
              px: 2,
              minWidth: 'fit-content',
              '& .MuiButton-startIcon': {
                margin: 0,
              },
            }}
          >
            {t('common.search', 'Search')}
          </Button>
        </Stack>
        <Popover
          open={imageSearchOpen}
          anchorEl={imageSearchAnchorEl}
          onClose={onCloseSearch}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 1,
              width: {
                xs: 'calc(100vw - 64px)',
                md: 520,
              },
              maxHeight: {
                xs: 420,
                md: 560,
              },
              overflowY: 'auto',
              p: 1.25,
            },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            {imageSearchResults.map(result => (
              <ButtonBase
                key={result.imageUrl}
                onClick={() => onUseSearchResult(result)}
                disabled={saving || imageMutating}
                sx={theme => ({
                  width: '100%',
                  aspectRatio: '1 / 1',
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'common.white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: theme.palette.action.hover,
                  },
                })}
              >
                <Box
                  component="img"
                  src={result.thumbnailUrl}
                  alt={result.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </ButtonBase>
            ))}
          </Box>
        </Popover>
        <Button
          variant="outlined"
          startIcon={<UploadFile />}
          onClick={onUploadClick}
          disabled={saving || imageMutating}
        >
          {t(
            'correction.productDialog.uploadFromComputer',
            'Upload from computer',
          )}
        </Button>
        {marketingImages.length ? (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {t(
                'correction.productDialog.currentImages',
                'Current marketing images',
              )}
            </Typography>
            <Stack spacing={1}>
              {marketingImages.map(image => {
                const isPrimary = effectivePrimaryImageId === image.id
                return (
                  <Stack
                    key={image.id}
                    direction="row"
                    spacing={1.5}
                    sx={theme => ({
                      alignItems: 'center',
                      border: '1px solid',
                      borderColor: isPrimary
                        ? theme.palette.primary.main
                        : theme.palette.divider,
                      borderRadius: 2,
                      p: 1,
                    })}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 1,
                        bgcolor: 'common.white',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={image.uri}
                        alt={t(
                          'correction.productDialog.marketingImageAlt',
                          'Marketing image',
                        )}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flex: 1 }}
                    >
                      {isPrimary
                        ? t(
                            'correction.productDialog.primaryImage',
                            'Primary image',
                          )
                        : t(
                            'correction.productDialog.secondaryImage',
                            'Marketing image',
                          )}
                    </Typography>
                    <Tooltip
                      title={
                        isPrimary
                          ? t(
                              'correction.productDialog.primaryImageSelected',
                              'Primary image',
                            )
                          : t(
                              'correction.productDialog.setPrimaryImage',
                              'Set as primary',
                            )
                      }
                    >
                      <span>
                        <IconButton
                          color={isPrimary ? 'primary' : 'default'}
                          onClick={() => onSelectPrimaryImage(image)}
                          disabled={saving || imageMutating}
                        >
                          {isPrimary ? <Star /> : <StarBorder />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      color="error"
                      onClick={() => onDeleteImage(image.id)}
                      disabled={saving || imageMutating}
                    >
                      {imageDeletingId === image.id ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DeleteOutline />
                      )}
                    </IconButton>
                  </Stack>
                )
              })}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  )
}

export default ProductDialogImagePanel
